import { Button, Checkbox, Form, Input, Select } from 'antd';
import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BrowserStorageUtil, cookieStorage } from '@swift-247/s247.library.ui-core';
import buildTimeConfig from '../../../config/buildtime.config';
import config from '../../../config/config';
import { getPartnerBannerImages, isWebView, redirectToBooking, WEBVIEW_BYPASS_PHONE_KEY } from '../../utilities/webview';
import PartnerBannerCarousel from '../../components/PartnerBannerCarousel';
import TermsDrawer from '../../components/TermsDrawer';
import iconChevronRightUrl from '../../assets/icon/chevron-right.svg';
import iconWarningUrl from '../../assets/icon/warning.svg';
import {
  checkPhoneExistAction,
  requestNewOTPAction,
  resetStateAction,
  storeBizAccountCustomerCode
} from '../../store/login/actions';
import {
  OTP_CONFIRMED,
  OTP_NO_CONFIRMED,
  PASSWORD_EXIST,
  PHONE_NOT_EXIST
} from '../../store/login/actionTypes';
import {
  getCheckPhoneExistStatus,
  getLoginMessage,
  getLoginStatus
} from '../../store/login/selectors';
import { LANGUAGE_KEY } from '../../utilities/constant';

const initialMsgState = {
	phoneNumber: '',
	term: '',
};

const ValidatePhoneNumber = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [msg, setMsg] = useState(initialMsgState);
	const [isPersonal, setIsPersonal] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const status = useSelector(getCheckPhoneExistStatus);
	const loginMessage = useSelector(getLoginMessage);
	const loginStatus = useSelector(getLoginStatus);
	const [isBizAccount, setIsBizAccount] = useState(false);
	const { t, i18n } = useTranslation();
	const webView = isWebView();
	const [termDrawerOpen, setTermDrawerOpen] = useState(false);
	const [privacyDrawerOpen, setPrivacyDrawerOpen] = useState(false);

	const handleTermLinkClick = (openDrawer: (open: boolean) => void) => (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (!webView) return;
		e.preventDefault();
		openDrawer(true);
	};
	// Partner banner (e.g. Vikki) replaces the "Welcome to Swift247!" title when
	// configured via theme_config.backgroundImagesUrl — falls back to the title
	// when the partner hasn't set one. backgroundImagesUrl is keyed by language
	// (en/vi), so re-read it whenever the active language changes.
	const [bannerImages, setBannerImages] = useState<string[]>(() => getPartnerBannerImages(i18n.language));

	useEffect(() => {
		setBannerImages(getPartnerBannerImages(i18n.language));
	}, [i18n.language]);
	const { Option } = Select;
	const accounts = [
		{
			label: 'personal_account',
			value: 'personal',
		},
		{
			label: 'business_account',
			value: 'business',
		},
	];

	const handleChangeAccount = (e: any) => {
		setIsPersonal(e.target.value === 'personal');
		form.resetFields(['phoneNumber']);
		setMsg({ phoneNumber: '', term: '' });
	};

	// Partner webview bypass: a phone was supplied but no account exists yet
	// (index.tsx stashed it since it couldn't resolve a bypass token). Prefill
	// the phone field via Form.Item's own `initialValue` so the user lands on
	// this screen with it ready to review and submit, instead of auto-submitting
	// straight to the OTP screen.
	// Previously this used form.setFieldsValue() in a useEffect + a manual
	// defaultValue on the raw Input — that relied on the `phoneNumber`
	// Form.Item's single child being the actual Input, but it was wrapped in a
	// <div> (to lay out the error message alongside it), so rc-field-form's
	// auto-injected value/onChange landed on that div instead of the Input.
	// The value only ever reached the Form's internal store because
	// setFieldsValue writes there directly — real submits (form.getFieldsValue()
	// in validateForm()) worked by luck of timing, not because the field was
	// genuinely wired up, and the exact same wrapping is why the OS/keyboard's
	// own phone-number autofill (not just this bypass path) could visually fill
	// the box while the Form never saw the value: autofill sets the DOM input's
	// value directly, and with no onChange reaching the Form there's nothing to
	// pick that change up either. Form.Item now wraps the Input directly (see
	// JSX below) so both paths are wired correctly from the start.
	const [bypassPhoneParts] = useState(() => {
		const bypassPhone = sessionStorage.getItem(WEBVIEW_BYPASS_PHONE_KEY);
		if (!bypassPhone) return null;
		sessionStorage.removeItem(WEBVIEW_BYPASS_PHONE_KEY);
		let phoneNumber = bypassPhone;
		if (_.startsWith(phoneNumber, '84')) {
			phoneNumber = phoneNumber.substring(2);
		} else if (_.startsWith(phoneNumber, '0')) {
			phoneNumber = phoneNumber.substring(1);
		}
		return { prefix: '84', phoneNumber };
	});

	useEffect(() => {
		if (loginStatus) {
			// Verify the actual token still exists — Redux sessionStorage outlives cookie/localStorage clearing
			const envStorage = buildTimeConfig.env !== 'local' ? localStorage : cookieStorage;
			const authInfo = new BrowserStorageUtil(envStorage as any).deserializedGet('authInfo');
			if (authInfo?.accessToken) {
				redirectToBooking();
			} else {
				dispatch(resetStateAction());
			}
		} else {
			dispatch(resetStateAction());
		}
	}, [loginStatus]);

	const handleFormChange = () => {
		validateForm();
	};

	const validateForm = () => {
		const { phoneNumber, term } = form.getFieldsValue();
		setMsg({ phoneNumber: '', term: '' });

		if (!phoneNumber || !phoneNumber.length) {
			const msgErr = isPersonal
				? 'validation_required_phone'
				: 'validation_required_customer';
			setMsg({ ...msg, phoneNumber: msgErr });
			return false;
		}

		let isValid = true;
		// check biz account
		if (!isPersonal) {
			setIsBizAccount(true);
			return true;
		}

		setIsBizAccount(false);
		if (phoneNumber.length < 9) {
			setMsg({ ...msg, phoneNumber: 'validation_phone_length_min' });
			isValid = false;
		}
		if (!term) {
			setMsg({ ...msg, term: 'validation_term_service' });
			isValid = false;
		}
		return isValid;
	};

	const getPhoneNumberValue = () => {
		const { prefix, phoneNumber } = form.getFieldsValue();
		let phone = phoneNumber;
		if (isPersonal) {
			if (_.startsWith(phoneNumber, '0')) {
				phone = phoneNumber.substring(1);
			}
			phone = prefix + phone;
		}
		return phone;
	};

	const handleOnSubmit = () => {
		if (validateForm()) {
			setIsLoading(true);
			dispatch(checkPhoneExistAction(getPhoneNumberValue()));
		}
	};

	useEffect(() => {}, [loginMessage]);

	useEffect(() => {
		setIsLoading(false);
		switch (status) {
			case PHONE_NOT_EXIST:
				// new customer account, redirect to OTP page
				// OTP auto send from api check_phone
				// biz account not exist, only show message, not redirect
				if (!isBizAccount) {
					navigate('/enter-otp');
				}
				break;
			case OTP_NO_CONFIRMED:
			case OTP_CONFIRMED:
				// phone is invalid, move to OTP to create new account
				const phoneValue = getPhoneNumberValue();
				if (isBizAccount) {
					phoneValue &&
						dispatch(
							storeBizAccountCustomerCode({
								customerCode: phoneValue,
							}),
						);
				}
				// request new OTP here
				phoneValue && dispatch(requestNewOTPAction(phoneValue));
				navigate('/enter-otp');
				break;
			case PASSWORD_EXIST:
				// phone is valid, move to step 2 to enter password
				navigate('/enter-password');
				break;
			default:
				break;
		}
	}, [status]);

	const registerNow = () => {
		navigate('/register');
	};

	const prefixSelector = (
		<Form.Item name="prefix" noStyle>
			<Select
				style={{
					width: 70,
				}}
			>
				<Option value="82">+82</Option>
				<Option value="84">+84</Option>
			</Select>
		</Form.Item>
	);

	return (
		<>
			{bannerImages.length ? (
				<PartnerBannerCarousel images={bannerImages} />
			) : (
				<div className="header-title">
					<div className="title">{t('global_welcome')}</div>
					<div className="subtitle">{t('section_title_login')}</div>
				</div>
			)}
			<Form
				layout="vertical"
				initialValues={{ term: true, prefix: '84' }}
				onValuesChange={() => handleFormChange()}
				form={form}
			>
				<Form.Item className="account-form">
					<div className="radio-button-group mts">
						{accounts.map((account, i) => {
							return (
								<div key={account.value} className="item">
									<input
										type="radio"
										name="button-group"
										className="radio-button"
										value={account.value}
										id={account.value}
										onChange={handleChangeAccount}
										defaultChecked={i === 0}
									/>
									<label htmlFor={account.value}>
										{t(account.label)}
									</label>
								</div>
							);
						})}
					</div>
				</Form.Item>
				<Form.Item
					className="phone-form"
					help={t(msg.phoneNumber)}
					name="phoneNumber"
					initialValue={bypassPhoneParts?.phoneNumber}
				>
					<Input
						addonBefore={isPersonal ? prefixSelector : null}
						placeholder={t(
							isPersonal
								? 'placeholder_phone'
								: 'placeholder_enter_customer_code',
						)}
						type="number"
						onKeyPress={(e: any) => {
							const lengthPhone = isPersonal ? 11 : 8;
							if (e.target.value.length === lengthPhone) {
								e.preventDefault();
								return false;
							}
						}}
						min={0}
						maxLength={8}
						autoFocus
					/>
				</Form.Item>
				{loginMessage && (
					<div className="error-msg">
						<img src={iconWarningUrl} />{' '}
						<span>{t(loginMessage)}</span>
					</div>
				)}

				<Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						loading={isLoading}
						disabled={isLoading}
						onClick={handleOnSubmit}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                handleOnSubmit();
              }
            }}
					>
						{t('button_next')}{' '}
						<img
							src={iconChevronRightUrl}
							width="16"
							className="ms-1"
						/>
					</Button>
				</Form.Item>

				<Form.Item
					help={t(msg.term)}
					valuePropName="checked"
					name="term"
				>
					<Checkbox>
						{t('authentication_message_term_service_1')}{' '}
						<a
							target={webView ? undefined : '_blank'}
							className="term-link"
							href={
								config.termCondition[
									(i18n.language || 'en') as LANGUAGE_KEY
								]
							}
							onClick={handleTermLinkClick(setTermDrawerOpen)}
						>
							{t('authentication_message_term_service_2')}
						</a>{' '}
						{t('authentication_message_term_service_3')}{' '}
						<a
							target={webView ? undefined : '_blank'}
							className="term-link"
							href={
								config.privatePolicy[
									(i18n.language || 'en') as LANGUAGE_KEY
								]
							}
							onClick={handleTermLinkClick(setPrivacyDrawerOpen)}
						>
							{t('authentication_message_term_service_4')}
						</a>
					</Checkbox>
				</Form.Item>
				<TermsDrawer
					href={config.termCondition[(i18n.language || 'en') as LANGUAGE_KEY]}
					open={termDrawerOpen}
					onOpenChange={setTermDrawerOpen}
					title={t('authentication_message_term_service_2')}
				/>
				<TermsDrawer
					href={config.privatePolicy[(i18n.language || 'en') as LANGUAGE_KEY]}
					open={privacyDrawerOpen}
					onOpenChange={setPrivacyDrawerOpen}
					title={t('authentication_message_term_service_4')}
				/>

					<Form.Item style={{visibility: isPersonal ? 'hidden' : 'visible'}}>
						<b>{t('login_message_business_account_hint')} </b>
						<a
							className="text-decoration-underline"
							onClick={() => registerNow()}
						>
							{t('login_message_business_account_register')}
						</a>
					</Form.Item>

			</Form>
		</>
	);
};
export default ValidatePhoneNumber;
